//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, Typography, useTheme } from '@mui/material'
import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement } from 'react'
import { useParams } from 'react-router-dom'

import { OtStoreState, useOtDispatch, useOtSelector } from '@react/redux/store'

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import DoneIcon from '@mui/icons-material/Done'
import { PoolLifeCycleState, PoolTypeEnum } from '@shared/Enums'
import { PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import LogoUpload from '@react/components/LogoUpload'
import PoolLegalPage from '@react/components/pools/PoolLegalPage'
import AdminGrantPoolAccessPage from './AdminGrantPoolAccessPage'
import { PoolStateChangeEnum, poolStateChangeTx } from '@react/signedTxs/poolStateChangeTx'
import { displayName } from '@shared/PoolBaseCTO'

export default function AdminPoolActivationChecklist(props: { poolAddr?: EthAddr }): ReactElement {
  const p = useParams().poolAddr
  const poolAddr = (props.poolAddr ? props.poolAddr : p) as EthAddr
  const dispatch = useOtDispatch()

  const theme = useTheme()

  const poolBaseOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  )
  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null

  const [hasIcon, setHasIcon] = React.useState<boolean | undefined>(undefined)
  const [hasSomeGranted, setHasSomeGranted] = React.useState<boolean | undefined>(undefined)
  const [hasRequiredDocs, setHasRequiredDocs] = React.useState<boolean | undefined>(undefined)

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
  }
  return (
    <Box marginTop={20} px={3} py={2} sx={{ flexGrow: 1 }}>
      <Grid alignItems={'stretch'} direction={'column'} container spacing={8} sx={{ flexGrow: 1 }}>
        <Grid item xs={12} key={'name'}>
          <Typography variant="h1" margin="dense">
            {displayName(poolBaseOverviewCTO)}
          </Typography>
        </Grid>
        <Grid container item marginTop={10} xs={12} key={'icon'}>
          <Grid item xs={1}>
            {hasIcon && <DoneIcon sx={{ color: theme.palette.success.main }} />}
            {!hasIcon && <CheckBoxOutlineBlankIcon sx={{ color: theme.palette.success.main }} />}
          </Grid>
          <Grid item xs={3}>
            <Typography variant="overline" margin="dense">
              Pool Icon
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <LogoUpload size={50} addrOrId={poolAddr} setHasIcon={setHasIcon} />
          </Grid>
        </Grid>
        <Grid container item marginTop={10} xs={12} key={'access'}>
          <Grid item xs={1}>
            {hasSomeGranted && <DoneIcon sx={{ color: theme.palette.success.main }} />}
            {!hasSomeGranted && <CheckBoxOutlineBlankIcon sx={{ color: theme.palette.success.main }} />}
          </Grid>
          <Grid item xs={3}>
            <Typography variant="overline" margin="dense">
              Grant Access to Lenders
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <AdminGrantPoolAccessPage poolAddr={poolAddr} setHasSomeGranted={setHasSomeGranted} />
          </Grid>
        </Grid>

        <Grid container item marginTop={10} xs={12} key={'docs'}>
          <Grid item xs={1}>
            {hasRequiredDocs && <DoneIcon sx={{ color: theme.palette.success.main }} />}
            {!hasRequiredDocs && <CheckBoxOutlineBlankIcon sx={{ color: theme.palette.success.main }} />}
          </Grid>
          <Grid item xs={3}>
            <Typography variant="overline" margin="dense">
              Pool Documentation
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <PoolLegalPage poolAddr={poolAddr} setHasRequiredDocs={setHasRequiredDocs} />
          </Grid>
        </Grid>
        <Grid container item marginTop={10} xs={12} key={'firstLoss'}>
          <Grid item xs={1}>
            {poolOverviewCTO && poolOverviewCTO?.state !== PoolLifeCycleState.Initialized && (
              <DoneIcon sx={{ color: theme.palette.success.main }} />
            )}
            {poolOverviewCTO && poolOverviewCTO?.state === PoolLifeCycleState.Initialized && (
              <CheckBoxOutlineBlankIcon sx={{ color: theme.palette.success.main }} />
            )}
            {poolFlexOverviewCTO && poolFlexOverviewCTO?.state !== PoolLifeCycleState.Initialized && (
              <DoneIcon sx={{ color: theme.palette.success.main }} />
            )}
            {poolFlexOverviewCTO && poolFlexOverviewCTO?.state === PoolLifeCycleState.Initialized && (
              <CheckBoxOutlineBlankIcon sx={{ color: theme.palette.success.main }} />
            )}
          </Grid>
          <Grid item xs={3}>
            <Typography variant="overline" margin="dense">
              Activate Vault
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Button
              sx={sx}
              onClick={() => {
                dispatch(poolStateChangeTx({ poolAddr, poolState: PoolStateChangeEnum.ACTIVATE }))
              }}
              variant={'contained'}
            >
              <Typography variant={'buttonBold'}>Activate Vault</Typography>
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}
